import React, { useMemo } from 'react';
import { View, Text, StyleSheet, Dimensions } from 'react-native';
import Svg, { Path, Rect, Text as SvgText, Line } from 'react-native-svg';
import { BubbleData } from './BubbleComponent';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { ReactNativeZoomableView } from '@openspacelabs/react-native-zoomable-view';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import parseByDelimiter from '@utils/parseByDelimiter';

interface Connection {
  from: string;
  to: string;
  commonFields: string[];
  strength: number;
}

interface SankeyNode {
  id: string;
  title: string;
  value: number;
  x: number;
  y: number;
  width: number;
  height: number;
  color: string;
  importance: number; // Based on data count and connections
}

interface SankeyLink {
  source: string;
  target: string;
  value: number;
  path: string;
  color: string;
}

interface SankeyDiagramProps {
  bubbles: BubbleData[];
  connections: Connection[];
  width: number;
  height: number;
  onNodeTap?: (bubble: BubbleData) => void;
}

const SankeyDiagram: React.FC<SankeyDiagramProps> = ({
  bubbles,
  connections,
  width,
  height,
  onNodeTap
}) => {
  const theme = themeSelector();

  // Calculate importance scores for each bubble
  const calculateImportance = (bubble: BubbleData): number => {
    const connectionCount = connections.filter(
      conn => conn.from === bubble._id || conn.to === bubble._id
    ).length;
    
    // Get max values for normalization
    const maxDataCount = Math.max(...bubbles.map(b => b.count || 0));
    const maxConnections = Math.max(...bubbles.map(b => 
      connections.filter(conn => conn.from === b._id || conn.to === b._id).length
    ));
    
    // Normalize both factors to 0-1 scale
    const normalizedDataWeight = maxDataCount > 0 ? (bubble.count || 0) / maxDataCount : 0;
    const normalizedConnectionWeight = maxConnections > 0 ? connectionCount / maxConnections : 0;
    
    // Combine with equal weighting (0.5 each) plus small minimum
    const importance = (normalizedDataWeight * 0.5) + (normalizedConnectionWeight * 0.5);
    
    return Math.max(importance, 0.01); // Minimum threshold to avoid zero values
  };

  // Generate Sankey nodes and links
  const { nodes, links, columnLabels } = useMemo(() => {
    if (!bubbles.length) return { nodes: [], links: [], columnLabels: [] };

    // Sort bubbles by importance
    const sortedBubbles = bubbles
      .map(bubble => ({
        ...bubble,
        importance: calculateImportance(bubble)
      }))
      .sort((a, b) => b.importance - a.importance);

    // Dynamic categorization based on data distribution
    const totalBubbles = sortedBubbles.length;
    const highImportanceThreshold = Math.ceil(totalBubbles * 0.33); // Top 33%
    const mediumImportanceThreshold = Math.ceil(totalBubbles * 0.66); // Middle 33%
    
    // Create columns based on dynamic importance tiers
    const highImportance = sortedBubbles.slice(0, highImportanceThreshold);
    const mediumImportance = sortedBubbles.slice(highImportanceThreshold, mediumImportanceThreshold);
    const lowImportance = sortedBubbles.slice(mediumImportanceThreshold);

    // Ensure we have at least one item in each category if we have 3+ items
    let columns = [];
    let labels = [];
    if (totalBubbles >= 3) {
      columns = [highImportance, mediumImportance, lowImportance].filter(col => col.length > 0);
      labels = ['High Importance', 'Medium Importance', 'Low Importance'].slice(0, columns.length);
    } else if (totalBubbles >= 2) {
      columns = [highImportance, lowImportance].filter(col => col.length > 0);
      labels = ['Higher Importance', 'Lower Importance'].slice(0, columns.length);
    } else {
      columns = [sortedBubbles];
      labels = ['Data Items'];
    }
    
    // Calculate node dimensions with more spacing for labels
    const nodeWidth = 60; // Increased width
    const columnWidth = width / Math.max(columns.length, 1);
    const padding = 40; // Increased padding for labels
    const availableHeight = height - padding * 2;

    const sankeyNodes: SankeyNode[] = [];
    
    columns.forEach((column, colIndex) => {
      const nodeHeight = Math.max(15, Math.min(availableHeight / column.length - 20, 80)); // Increased spacing
      const startY = padding + (availableHeight - (column.length * nodeHeight + (column.length - 1) * 20)) / 2;
      
      column.forEach((bubble, nodeIndex) => {
        const x = colIndex * columnWidth + columnWidth / 2 - nodeWidth / 2;
        const y = startY + nodeIndex * (nodeHeight + 20); // Increased vertical spacing
        
        sankeyNodes.push({
          id: bubble._id,
          title: bubble.dataTitle,
          value: bubble.count || 0,
          x,
          y,
          width: nodeWidth,
          height: nodeHeight,
          color: bubble.color,
          importance: bubble.importance
        });
      });
    });

    // Create links between connected nodes
    const sankeyLinks: SankeyLink[] = connections
      .filter(conn => {
        const sourceNode = sankeyNodes.find(n => n.id === conn.from);
        const targetNode = sankeyNodes.find(n => n.id === conn.to);
        return sourceNode && targetNode && sourceNode.x < targetNode.x; // Only left-to-right flows
      })
      .map(conn => {
        const sourceNode = sankeyNodes.find(n => n.id === conn.from)!;
        const targetNode = sankeyNodes.find(n => n.id === conn.to)!;
        
        const linkValue = conn.strength * 100;
        const strokeWidth = Math.max(linkValue * 0.3, 2);
        
        // Create curved path
        const x1 = sourceNode.x + sourceNode.width;
        const y1 = sourceNode.y + sourceNode.height / 2;
        const x2 = targetNode.x;
        const y2 = targetNode.y + targetNode.height / 2;
        
        const cx1 = x1 + (x2 - x1) * 0.6;
        const cx2 = x2 - (x2 - x1) * 0.6;
        
        const path = `M ${x1} ${y1} C ${cx1} ${y1} ${cx2} ${y2} ${x2} ${y2}`;
        
        return {
          source: conn.from,
          target: conn.to,
          value: linkValue,
          path,
          color: theme ? 'rgba(255, 107, 107, 0.6)' : 'rgba(78, 205, 196, 0.6)'
        };
      });

    return { nodes: sankeyNodes, links: sankeyLinks, columnLabels: labels };
  }, [bubbles, connections, width, height, theme]);

  // Handle node tap
  const handleNodeTap = (nodeId: string) => {
    const bubble = bubbles.find(b => b._id === nodeId);
    if (bubble && onNodeTap) {
      onNodeTap(bubble);
    }
  };

  return (
    <View style={styles.container}>
      {/* Title */}
      <Text style={[styles.title, { color: theme ? '#333' : '#fff' }]}>
        Data Flow Diagram - Importance Based
      </Text>
      
      {/* Column Labels */}
      <View style={styles.labelContainer}>
        {nodes.length > 0 && columnLabels.map((label, index) => (
          <Text key={index} style={[styles.columnLabel, { color: theme ? '#666' : '#ccc' }]}>
            {label}
          </Text>
        ))}
      </View>

      {/* Interactive Zoomable Container */}
      <GestureHandlerRootView style={{ flex: 1 }}>
        <ReactNativeZoomableView
          maxZoom={3}
          minZoom={0.5}
          initialZoom={1}
          bindToBorders={true}
          contentHeight={height}
          contentWidth={width}
          style={{ flex: 1 }}
        >
          <Svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
            {/* Draw links first (behind nodes) */}
            {links.map((link, index) => (
              <Path
                key={`link-${index}`}
                d={link.path}
                fill="none"
                stroke={link.color}
                strokeWidth={Math.max(link.value * 0.1, 2)}
                opacity={0.7}
              />
            ))}

            {/* Draw nodes */}
            {nodes.map((node, index) => {
              const parsedTitle = parseByDelimiter(node.title) || node.title;
              const displayTitle = parsedTitle.length > 12 ? `${parsedTitle.substring(0, 12)}...` : parsedTitle;
              
              return (
                <React.Fragment key={node.id}>
                  {/* Node rectangle */}
                  <Rect
                    x={node.x}
                    y={node.y}
                    width={node.width}
                    height={node.height}
                    fill={node.color}
                    stroke={theme ? '#333' : '#fff'}
                    strokeWidth={2}
                    rx={5}
                    onPress={() => handleNodeTap(node.id)}
                  />
                  
                  {/* Node title - using parseByDelimiter */}
                  <SvgText
                    x={node.x + node.width / 2}
                    y={node.y - 12}
                    fontSize={10}
                    fill={theme ? '#333' : '#fff'}
                    textAnchor="middle"
                    fontWeight="bold"
                  >
                    {displayTitle}
                  </SvgText>
                  
                  {/* Node value */}
                  <SvgText
                    x={node.x + node.width / 2}
                    y={node.y + node.height / 2 + 4}
                    fontSize={9}
                    fill="white"
                    textAnchor="middle"
                    fontWeight="bold"
                  >
                    {node.value}
                  </SvgText>
                  
                  {/* Importance score */}
                  <SvgText
                    x={node.x + node.width / 2}
                    y={node.y + node.height + 20}
                    fontSize={8}
                    fill={theme ? '#666' : '#ccc'}
                    textAnchor="middle"
                  >
                    {node.importance.toFixed(2)}
                  </SvgText>
                </React.Fragment>
              );
            })}
          </Svg>
        </ReactNativeZoomableView>
      </GestureHandlerRootView>

      {/* Legend */}
      <View style={styles.legend}>
        <Text style={[styles.legendTitle, { color: theme ? '#333' : '#fff' }]}>
          Categorization Legend
        </Text>
        <View style={styles.legendItems}>
          <View style={styles.legendItem}>
            <View style={[styles.legendLine, { backgroundColor: theme ? 'rgba(255, 107, 107, 0.6)' : 'rgba(78, 205, 196, 0.6)' }]} />
            <Text style={[styles.legendText, { color: theme ? '#666' : '#ccc' }]}>
              Data Relationship Flow
            </Text>
          </View>
          <View style={styles.legendItem}>
            <Text style={[styles.legendText, { color: theme ? '#666' : '#ccc' }]}>
              Numbers show data count
            </Text>
          </View>
          <View style={styles.legendItem}>
            <Text style={[styles.legendText, { color: theme ? '#666' : '#ccc' }]}>
              Categories by data size + connections
            </Text>
          </View>
          <View style={styles.legendItem}>
            <Text style={[styles.legendText, { color: theme ? '#666' : '#ccc' }]}>
              Flow thickness = Connection strength
            </Text>
          </View>
        </View>
      </View>

      {/* Empty state */}
      {nodes.length === 0 && (
        <View style={styles.emptyState}>
          <Text style={[styles.emptyText, { color: theme ? '#666' : '#ccc' }]}>
            No data available for Sankey diagram
          </Text>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingTop: 10,
  },
  title: {
    fontSize: 18,
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 10,
  },
  labelContainer: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    paddingHorizontal: 20,
    marginBottom: 15,
    minHeight: 20,
  },
  columnLabel: {
    fontSize: 12,
    fontWeight: '600',
    textAlign: 'center',
    flex: 1,
  },
  legend: {
    position: 'absolute',
    bottom: 10,
    left: 10,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 10,
    padding: 12,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.2)',
    maxWidth: 200,
  },
  legendTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  legendItems: {
    gap: 5,
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  legendLine: {
    width: 20,
    height: 3,
    borderRadius: 1.5,
  },
  legendText: {
    fontSize: 9,
    flex: 1,
  },
  emptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptyText: {
    fontSize: 16,
    textAlign: 'center',
  },
});

export default SankeyDiagram;
